/**
 * Shared overlay components for thumbnail cards — badges, download buttons,
 * and the three-dot context-menu trigger. Consumed by SceneItem and
 * AIStudioScenesPanel to avoid duplicating these styled patterns.
 */
import React from 'react'
import { styled } from 'baseui'
import ThreeDotsIcon from '@/scenes/Editor/components/Icons/ThreeDotsIcon'
import { lightTheme } from '@/customTheme'

// ---------------------------------------------------------------------------
// Bottom-left badge slot
// ---------------------------------------------------------------------------

export const ThumbnailBottomLeftBadgeWrapper = styled('div', {
  position: 'absolute',
  left: '8px',
  bottom: '8px',
  zIndex: 100,
  display: 'flex',
  pointerEvents: 'none',
})

export const ThumbnailOrderBadge = styled('div', {
  width: '19px',
  height: '19px',
  background: 'rgba(255, 255, 255, .4)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: '600',
  lineHeight: '10px',
  pointerEvents: 'none',
})

export const ThumbnailBottomRightBadgeWrapper = styled('div', {
  position: 'absolute',
  right: '8px',
  bottom: '8px',
  zIndex: 100,
  display: 'flex',
  pointerEvents: 'none',
})

// ---------------------------------------------------------------------------
// Download badge button
// ---------------------------------------------------------------------------

export const ThumbnailDownloadBadge = styled('div', (props: { $disabled?: boolean }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '270px',
  background: 'white',
  boxShadow: '0px 3px 9px 0px rgba(0, 0, 0, 0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: props.$disabled ? 'default' : 'pointer',
  pointerEvents: props.$disabled ? 'none' : 'auto',
  ':hover': {
    boxShadow: props.$disabled ? '0px 3px 9px 0px rgba(0, 0, 0, 0.12)' : '0px 3px 9px 0px rgba(0, 0, 0, 0.2)',
  },
  outline: 'none',
  // Matches the scene item's keyboard focus ring.
  ':focus-visible': {
    outline: `2px solid ${lightTheme.colors.primary}`,
    outlineOffset: '2px',
  },
}))

// ---------------------------------------------------------------------------
// Three-dot context-menu trigger
// ---------------------------------------------------------------------------

const ThumbnailMenuButtonBase = styled('div', (props: { $isOpen: boolean; $show: boolean }) => ({
  position: 'absolute',
  top: '6px',
  right: '6px',
  borderRadius: '50%',
  width: '20px',
  height: '20px',
  background: props.$isOpen ? 'rgba(204, 4, 77, 1)' : 'rgba(17, 17, 17, 0.5)',
  display: 'flex',
  opacity: props.$show || props.$isOpen ? '1' : '0',
  justifyContent: 'center',
  alignItems: 'center',
  ':hover': {
    background: props.$isOpen ? 'rgba(204, 4, 77, 1)' : 'rgba(255, 5, 96, 1)',
  },
  ':active': {
    background: 'rgba(204, 4, 77, 1) !important',
  },
}))

export type ThumbnailMenuButtonProps = {
  $isOpen: boolean
  $show: boolean
  id: string
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  onMouseEnter: () => void
}

export const ThumbnailMenuButton = React.forwardRef<HTMLDivElement, ThumbnailMenuButtonProps>(
  (props, ref) => (
    <ThumbnailMenuButtonBase ref={ref} {...props}>
      <ThreeDotsIcon />
    </ThumbnailMenuButtonBase>
  ),
)
ThumbnailMenuButton.displayName = 'ThumbnailMenuButton'
